import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getNotifications, markNotificationsRead } from '../utils/api';
import { useWindowSize } from '../utils/useWindowSize';
import { FiGrid, FiUsers, FiCalendar, FiHeart, FiBook, FiLogOut, FiBell, FiMenu, FiX, FiRadio, FiBarChart2, FiActivity } from 'react-icons/fi';

const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useWindowSize();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);

  useEffect(function() {
    fetchNotifications();
    var interval = setInterval(fetchNotifications, 60000);
    return function() { clearInterval(interval); };
  }, []);

  // Close sidebar when navigating on mobile
  useEffect(function() {
    if (isMobile) setMobileOpen(false);
  }, [location.pathname, isMobile]);

  var fetchNotifications = async function() {
    try {
      var res = await getNotifications();
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unreadCount || 0);
    } catch (err) {
      // silently fail
    }
  };

  var handleMarkRead = async function() {
    try {
      await markNotificationsRead();
      setUnreadCount(0);
      setNotifications(function(prev) { return prev.map(function(n) { return Object.assign({}, n, { read: true }); }); });
    } catch (err) {
      // silently fail
    }
  };

  var handleNavClick = function(path) {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  const navItems = [
    { icon: <FiGrid />, label: 'Dashboard', path: '/dashboard' },
    { icon: <FiUsers />, label: 'Users', path: '/users' },
    { icon: <FiCalendar />, label: 'Sessions', path: '/sessions' },
    { icon: <FiHeart />, label: 'Healing Requests', path: '/healing-requests' },
    { icon: <FiBarChart2 />, label: 'Finance', path: '/finance' },
    { icon: <FiBook />, label: 'Course Completions', path: '/course-completions' },
    { icon: <FiActivity />, label: 'Practitioner Activities', path: '/practitioner-activities' },
    { icon: <FiRadio />, label: 'Announcements', path: '/announcements' },
  ];

  var notifTypeColors = {
    info: '#2980b9',
    warning: '#d4ac0d',
    urgent: '#e74c3c'
  };

  var sidebarStyle = {
    width: '260px',
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #071e12 0%, #0c2617 100%)',
    borderRight: '1px solid rgba(255,255,255,0.06)',
    display: 'flex',
    flexDirection: 'column',
    padding: '0',
    position: 'fixed',
    top: 0,
    left: isMobile ? (mobileOpen ? 0 : '-260px') : 0,
    bottom: 0,
    overflowY: 'auto',
    fontFamily: "'Open Sans', sans-serif",
    zIndex: 1000,
    transition: isMobile ? 'left 0.3s ease' : 'none'
  };

  return (
    <>
      {/* Hamburger button — mobile only */}
      {isMobile && !mobileOpen && (
        <button
          onClick={function() { setMobileOpen(true); }}
          style={{
            position: 'fixed', top: '14px', left: '16px', zIndex: 1001,
            background: '#4a8f82', border: 'none', borderRadius: '6px',
            width: '36px', height: '36px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: '18px', boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}>
          <FiMenu />
        </button>
      )}

      {/* Dark backdrop — mobile only when open */}
      {isMobile && mobileOpen && (
        <div
          onClick={function() { setMobileOpen(false); }}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.5)', zIndex: 999
          }}
        />
      )}

      {/* Sidebar */}
      <div style={sidebarStyle}>
        {/* Close button — mobile only */}
        {isMobile && (
          <button
            onClick={function() { setMobileOpen(false); }}
            style={{
              position: 'absolute', top: '16px', right: '16px',
              background: 'transparent', border: 'none',
              color: 'rgba(255,255,255,0.7)', fontSize: '20px', cursor: 'pointer',
              zIndex: 1002, display: 'flex', alignItems: 'center'
            }}>
            <FiX />
          </button>
        )}

        {/* Logo */}
        <div style={styles.logoWrap}>
          <img
            src="https://pranicwellnesscentre.com/wp-content/uploads/2018/10/PH-Revised-Logo.png"
            alt="PWC"
            style={styles.logo}
          />
          <div style={styles.portalLabel}>PWC Oneness with All</div>
        </div>

        {/* User Info */}
        <div style={styles.userInfo}>
          <div style={styles.userAvatar}>
            {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
          </div>
          <div style={{ flex: 1 }}>
            <div style={styles.userName}>{user?.firstName} {user?.lastName}</div>
            <div style={styles.userRole}>{user?.role?.toUpperCase()}</div>
          </div>
          {/* Notification Bell */}
          <button onClick={function() { setShowNotifPanel(!showNotifPanel); if (!showNotifPanel && unreadCount > 0) { handleMarkRead(); } }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'relative', padding: '4px', color: 'rgba(255,255,255,0.6)', fontSize: '18px', display: 'flex', alignItems: 'center' }}>
            <FiBell />
            {unreadCount > 0 && (
              <span style={styles.notificationBadge}>{unreadCount}</span>
            )}
          </button>
        </div>

        {/* Notification Panel */}
        {showNotifPanel && (
          <div style={styles.notifPanel}>
            <div style={styles.notifHeader}>
              <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#666' }}>Notifications</span>
              <button onClick={function() { setShowNotifPanel(false); }} style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: '16px' }}>x</button>
            </div>
            <div style={styles.notifList}>
              {notifications.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '12px' }}>No notifications</div>
              ) : notifications.slice(0, 20).map(function(n, i) {
                return (
                  <div key={i} style={{ padding: '10px 14px', borderBottom: '1px solid #f0f0f0', background: n.read ? '#fff' : '#f9faf7' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: notifTypeColors[n.type] || '#2980b9', flexShrink: 0, marginTop: '4px' }}></div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#333', lineHeight: '1.5' }}>{n.message}</div>
                        <div style={{ fontSize: '10px', color: '#999', marginTop: '3px' }}>{new Date(n.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Divider */}
        <div style={styles.divider}></div>

        {/* Navigation */}
        <nav style={styles.nav}>
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={function() { handleNavClick(item.path); }}
              style={{
                ...styles.navItem,
                ...(location.pathname === item.path ? styles.navItemActive : {})
              }}
            >
              <span style={styles.navIcon}>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>

        {/* Divider */}
        <div style={styles.divider}></div>

        {/* Logout */}
        <button onClick={logout} style={styles.logoutBtn}>
          <span style={styles.navIcon}><FiLogOut /></span>
          Logout
        </button>

        {/* Footer */}
        <div style={styles.sidebarFooter}>
          Pranic Wellness Centre<br />
          Perungudi, Chennai
        </div>
      </div>
    </>
  );
};

const styles = {
  logoWrap: {
    padding: '28px 24px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.06)'
  },
  logo: {
    height: '40px',
    marginBottom: '8px'
  },
  portalLabel: {
    fontSize: '9px',
    fontWeight: '700',
    letterSpacing: '2.5px',
    textTransform: 'uppercase',
    color: '#8DC63F',
    fontFamily: "'Raleway', sans-serif",
    opacity: 0.9
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '20px 24px'
  },
  userAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #4a8f82, #5a9a6e)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Raleway', sans-serif",
    fontSize: '14px',
    fontWeight: '700',
    color: '#fff',
    flexShrink: 0
  },
  userName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#fff',
    fontFamily: "'Raleway', sans-serif"
  },
  userRole: {
    fontSize: '10px',
    color: '#8DC63F',
    letterSpacing: '1.5px',
    fontWeight: '700',
    fontFamily: "'Raleway', sans-serif"
  },
  divider: {
    height: '1px',
    background: 'rgba(255,255,255,0.06)',
    margin: '0'
  },
  nav: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 0'
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 24px',
    background: 'transparent',
    border: 'none',
    borderLeft: '3px solid transparent',
    color: 'rgba(255,255,255,0.55)',
    fontSize: '13px',
    fontWeight: '600',
    fontFamily: "'Raleway', sans-serif",
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.2s',
    letterSpacing: '0.3px'
  },
  navItemActive: {
    background: 'rgba(255,255,255,0.06)',
    borderLeftColor: '#4a8f82',
    color: '#fff'
  },
  navIcon: {
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center'
  },
  logoutBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 24px',
    background: 'transparent',
    border: 'none',
    borderLeft: '3px solid transparent',
    color: '#e74c3c',
    fontSize: '13px',
    fontWeight: '600',
    fontFamily: "'Raleway', sans-serif",
    cursor: 'pointer',
    textAlign: 'left',
    margin: '8px 0'
  },
  sidebarFooter: {
    marginTop: 'auto',
    padding: '20px 24px',
    fontSize: '11px',
    color: 'rgba(255,255,255,0.2)',
    lineHeight: '1.6',
    borderTop: '1px solid rgba(255,255,255,0.06)'
  },
  notificationBadge: {
    position: 'absolute', top: '-2px', right: '-4px',
    background: '#e74c3c', color: '#fff',
    borderRadius: '10px', padding: '1px 5px',
    fontFamily: "'Raleway', sans-serif",
    fontSize: '9px', fontWeight: '700', minWidth: '14px',
    textAlign: 'center', lineHeight: '14px'
  },
  notifPanel: {
    background: '#fff', borderRadius: '0 0 8px 8px',
    maxHeight: '360px', overflowY: 'auto',
    borderTop: '2px solid #4a8f82'
  },
  notifHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 14px', borderBottom: '1px solid #e0e0e0'
  },
  notifList: {
    maxHeight: '320px', overflowY: 'auto'
  }
};

export default Sidebar;
